import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { useTransaction } from "../../../../context/TransactionContext";
import { useNavigationLoading } from "../../../../context/NavigationLoadingContext";
import { CalendarEventType } from "../../../../types/calendar";
import {
  transformTransactionResponseToGetTransactionsResponse,
  transformTransactionToEvent,
} from "../../../../utils/transactionUtils";
import { replaceTransactionEvents } from "../calendarEventUtils";
import { useTypedCalendarEvent } from "../calendarViewTypes";

interface UseDeepLinkTargetArgs {
  timezone: string | undefined;
  salonServices: any[];
  setSelectedDate: (date: Date) => void;
  setDateRange: (range: { start: string; end: string }) => void;
  handleEventSelect: (event: CalendarEventType) => void;
  performPredictiveFetch: (date: Date) => Promise<void>;
  refetchMonthBuffer: (date: Date) => Promise<void>;
  setIsInitialLoading: (loading: boolean) => void;
  setIsLoading: (loading: boolean) => void;
}

/**
 * Open a specific appointment on arrival: in-app notifications pass
 * `location.state.targetTransactionId` (+ optional targetTab), staff emails
 * link `?transactionId=`. Navigates the calendar to the appointment's week,
 * then selects its event once it lands in `events`.
 */
export function useDeepLinkTarget({
  timezone,
  salonServices,
  setSelectedDate,
  setDateRange,
  handleEventSelect,
  performPredictiveFetch,
  refetchMonthBuffer,
  setIsInitialLoading,
  setIsLoading,
}: UseDeepLinkTargetArgs) {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchTransaction, upsertTransaction } = useTransaction();
  const { events, setEvents } = useTypedCalendarEvent();
  const { hideNavigationLoading } = useNavigationLoading();

  const [targetTransactionId, setTargetTransactionId] = useState<string | null>(
    null,
  );
  // Optional initial tab passed to TransactionDetailModal; null = "details".
  const [targetTab, setTargetTab] = useState<string | null>(null);
  const clearTargetTab = useCallback(() => setTargetTab(null), []);

  const handleNavigationToTransaction = useCallback(
    async (transactionId: string) => {
      try {
        const transactionResponse = await fetchTransaction(transactionId);

        if (transactionResponse?.transaction) {
          const firstService =
            transactionResponse.line_items.booked_services[0];
          if (!firstService) throw new Error("No booked services found");

          const appointmentStart = moment
            .utc(firstService.start_dt)
            .tz(timezone || "");
          const weekStart = appointmentStart.clone().startOf("week");
          const weekEnd = appointmentStart.clone().endOf("week").add(1, "day");

          const transactionData =
            transformTransactionResponseToGetTransactionsResponse(
              transactionResponse,
            );

          upsertTransaction(transactionData);

          // Add transaction to events if not already present
          const existingEvent = events.find(
            (event) =>
              "transactionId" in event && event.transactionId === transactionId,
          );
          if (!existingEvent) {
            const newEvents = transformTransactionToEvent(
              transactionData,
              timezone || "",
              salonServices,
            ) as CalendarEventType[];
            setEvents((prevEvents) =>
              replaceTransactionEvents(prevEvents, transactionId, newEvents),
            );
          }

          // Set date range and selected date to navigate to the appointment
          setDateRange({
            start: weekStart.format("YYYY-MM-DD"),
            end: weekEnd.format("YYYY-MM-DD"),
          });
          setSelectedDate(appointmentStart.toDate());
          setTargetTransactionId(transactionId);

          // Ensure the month buffer is fetched for the appointment's month now
          // (the predictive-fetch effect would also get there, later).
          await performPredictiveFetch(appointmentStart.toDate());
        }

        setIsInitialLoading(false);
        // NOTE: the cross-route overlay is hidden by the target-transaction
        // effect below — right after handleEventSelect(targetEvent) — so it
        // stays up until the modal is actually committing to render. Hiding
        // here would flicker (overlay gone, modal not yet on screen).
      } catch (error) {
        console.error("Error navigating to appointment:", error);
        // Fetch current buffer on error (background fetch)
        await refetchMonthBuffer(new Date());
        setIsInitialLoading(false);
        // Error path: the modal will never open, so clear the overlay here
        // to give the user a clean exit. The success path delegates to the
        // effect that fires when the matching event lands in `events`.
        hideNavigationLoading();
      }
    },
    [
      fetchTransaction,
      timezone,
      upsertTransaction,
      events,
      salonServices,
      setEvents,
      setDateRange,
      setSelectedDate,
      performPredictiveFetch,
      setIsInitialLoading,
      refetchMonthBuffer,
      hideNavigationLoading,
    ],
  );

  // The location effects below must fire only when the location changes, not
  // whenever the handler's identity does (it changes with `events`), so they
  // call the latest handler through a ref. Declared first so it's current.
  const navigateToTransactionRef = useRef(handleNavigationToTransaction);
  useEffect(() => {
    navigateToTransactionRef.current = handleNavigationToTransaction;
  }, [handleNavigationToTransaction]);

  // In-app notification: router state carries the target.
  const locationState = location.state as {
    targetTransactionId?: string;
    targetTab?: string;
  } | null;
  const { pathname } = location;
  useEffect(() => {
    if (locationState?.targetTransactionId) {
      setTargetTab(locationState.targetTab ?? null);
      navigateToTransactionRef.current(locationState.targetTransactionId);
      navigate(pathname, { replace: true });
    }
    // pathname / navigate only change together with location.state.
  }, [locationState, pathname, navigate]);

  // Deep-link from staff notification emails:
  // /admin/calendar?transactionId=<uuid>. Email links can't carry router state
  // (the in-app notification path uses location.state), so read the query param
  // on load, select the transaction, then strip it from the URL.
  const { search } = location;
  useEffect(() => {
    const transactionId = new URLSearchParams(search).get("transactionId");
    if (transactionId) {
      navigateToTransactionRef.current(transactionId);
      navigate(pathname, { replace: true });
    }
  }, [search, pathname, navigate]);

  // Select the target once its event is in `events`.
  useEffect(() => {
    if (!targetTransactionId) return;
    const targetEvent = events.find(
      (event) =>
        "transactionId" in event && event.transactionId === targetTransactionId,
    );

    if (targetEvent) {
      handleEventSelect(targetEvent);
      setTargetTransactionId(null);
      setIsLoading(false);
      // Hide the cross-route overlay LAST, only once the appointment modal is
      // committing to open. Hiding when the fetch finished caused a visible
      // flicker (gap between "events updated" and "modal renders"). The catch
      // path in handleNavigationToTransaction still hides on error, and
      // NavigationLoadingContext has a safety auto-hide as a final backstop.
      hideNavigationLoading();
    }
  }, [
    targetTransactionId,
    events,
    handleEventSelect,
    setIsLoading,
    hideNavigationLoading,
  ]);

  return { targetTab, clearTargetTab };
}
